import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Plus, CheckSquare, Square, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { Dialog } from 'primereact/dialog';
import api from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { Combobox } from '@/components/ui/combobox';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import type { BankAccount, BankReconciliation, BankTransaction } from '@/types/bank';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-warning/10 text-warning-text ring-warning',
  COMPLETED: 'bg-success/10 text-success-text ring-success',
};

const emptyForm = {
  bankAccountId: '',
  statementDate: new Date().toISOString().split('T')[0],
  openingBalance: '',
  closingBalance: '',
  notes: '',
};

interface ReconciliationListItem extends BankReconciliation {
  bankAccount?: { name: string };
  _count?: { transactions?: number };
}

interface ReconciliationListResult {
  data: ReconciliationListItem[];
  total: number;
  page: number;
  limit: number;
}

interface ReconciliationDetail extends BankReconciliation {
  bankAccount?: { name: string };
  summary?: { difference?: number; reconciledNet?: number };
  unreconciledTransactions?: BankTransaction[];
  transactions?: BankTransaction[];
}

interface CreateReconciliationPayload {
  bankAccountId: string;
  statementDate: string;
  openingBalance: number;
  closingBalance: number;
  notes: string;
}

export function BankReconciliationPage() {
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('finance');
  const canCreate = hasPermission('bank:write') || hasPermission('bank:*');

  const [newRecDialogOpen, setNewRecDialogOpen] = useState(false);
  const [selectedRecId, setSelectedRecId] = useState<string | null>(null);
  const [filterAccountId, setFilterAccountId] = useState('');
  const [form, setForm] = useState({ ...emptyForm });
  const queryClient = useQueryClient();

  const { data: bankAccounts = [] } = useQuery<BankAccount[]>({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const res = await api.get('/bank-accounts');
      return res.data ?? [];
    },
  });

  const { data: reconciliationResult, isLoading: listLoading } = useQuery<ReconciliationListResult>({
    queryKey: ['bank-reconciliations', filterAccountId],
    queryFn: async () => {
      const params = filterAccountId ? `?bankAccountId=${filterAccountId}` : '';
      const res = await api.get(`/bank-reconciliations${params}`);
      return res.data ?? { data: [], total: 0, page: 1, limit: 20 };
    },
  });
  const reconciliations: ReconciliationListItem[] = reconciliationResult?.data ?? [];

  const { data: selectedRec, isLoading: recLoading } = useQuery<ReconciliationDetail>({
    queryKey: ['bank-reconciliation', selectedRecId],
    queryFn: async () => {
      const res = await api.get(`/bank-reconciliations/${selectedRecId}`);
      return res.data;
    },
    enabled: !!selectedRecId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateReconciliationPayload) => api.post('/bank-reconciliations', data),
    onSuccess: () => {
      toast.success(t('reconciliationStarted'));
      queryClient.invalidateQueries({ queryKey: ['bank-reconciliations'] });
      setNewRecDialogOpen(false);
      setForm({ ...emptyForm });
    },
    onError: (err) => notify.error(err, t('failedToCreateReconciliation')),
  });

  const reconcileMutation = useMutation({
    mutationFn: ({ txId, reconcile }: { txId: string; reconcile: boolean }) =>
      api.post(`/bank-reconciliations/${selectedRecId}/transactions/${txId}/${reconcile ? 'reconcile' : 'unreconcile'}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bank-reconciliation', selectedRecId] }),
    onError: () => toast.error(t('failedToUpdateTransaction')),
  });

  const completeMutation = useMutation({
    mutationFn: () => api.patch(`/bank-reconciliations/${selectedRecId}/complete`),
    onSuccess: () => {
      toast.success(t('reconciliationCompleted'));
      queryClient.invalidateQueries({ queryKey: ['bank-reconciliations'] });
      queryClient.invalidateQueries({ queryKey: ['bank-reconciliation', selectedRecId] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
    onError: (err) => notify.error(err, t('cannotCompleteReconciliation')),
  });

  const handleCreate = () => {
    if (!form.bankAccountId || !form.statementDate || !form.openingBalance || !form.closingBalance) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    createMutation.mutate({
      ...form,
      openingBalance: Number(form.openingBalance),
      closingBalance: Number(form.closingBalance),
    });
  };

  const difference = selectedRec?.summary?.difference ?? 0;
  const isBalanced = Math.abs(difference) < 0.005;

  const [confirmComplete, setConfirmComplete] = useState(false);

  return (
    <div className="space-y-6 page-enter">
      {/* Header */}
      <PageHeader
        title={t('bankReconciliation')}
        subtitle={t('bankReconciliationSubtitle')}
        actions={canCreate && (
          <Button onClick={() => setNewRecDialogOpen(true)}>
            <Plus className="me-2 h-4 w-4" />
            {t('newReconciliation')}
          </Button>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: History + filter */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{t('filterByAccount')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Combobox
                options={[
                  { value: '', label: t('allAccounts') },
                  ...bankAccounts.map((b: BankAccount) => ({ value: b.id, label: b.name })),
                ]}
                value={filterAccountId}
                onChange={(val) => setFilterAccountId(val || '')}
                placeholder={t('selectAccount')}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{t('reconciliationHistory')}</CardTitle>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <SkeletonTable rows={4} cols={4} />
              ) : !reconciliations.length ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  {t('noReconciliationsYet')}
                </div>
              ) : (
                <div className="space-y-2">
                  {reconciliations.map((rec: ReconciliationListItem) => (
                    <button
                      key={rec.id}
                      onClick={() => setSelectedRecId(rec.id)}
                      className={cn(
                        'w-full text-start rounded-lg border p-3 text-sm transition-colors hover:bg-accent',
                        selectedRecId === rec.id && 'border-primary bg-primary/10',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{rec.bankAccount?.name}</span>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
                            STATUS_STYLES[rec.status],
                          )}
                        >
                          {rec.status === 'COMPLETED' ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <Clock className="h-3 w-3" />
                          )}
                          {rec.status}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(rec.statementDate).toLocaleDateString()} · {t('txCount', { count: rec._count?.transactions ?? 0 })}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Active reconciliation */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedRecId ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-20">
                <ArrowRightLeft className="h-12 w-12 text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground text-sm">{t('selectReconciliationToView')}</p>
              </CardContent>
            </Card>
          ) : recLoading ? (
            <Card>
              <CardContent className="py-12">
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-10 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : selectedRec ? (
            <>
              {/* Summary */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">
                      {selectedRec.bankAccount?.name} — {new Date(selectedRec.statementDate).toLocaleDateString()}
                    </CardTitle>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                        STATUS_STYLES[selectedRec.status],
                      )}
                    >
                      {selectedRec.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">{t('statementBalance')}</p>
                      <p className="font-bold text-lg">{formatCurrency(selectedRec.closingBalance)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('reconciledNet')}</p>
                      <p className="font-bold text-lg">
                        {formatCurrency(
                          Number(selectedRec.openingBalance) + (selectedRec.summary?.reconciledNet ?? 0),
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('difference')}</p>
                      <p
                        className={cn(
                          'font-bold text-lg',
                          isBalanced ? 'text-success-text' : 'text-destructive-text',
                        )}
                      >
                        {formatCurrency(difference)}
                      </p>
                    </div>
                  </div>

                  {selectedRec.status === 'DRAFT' && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={() => setConfirmComplete(true)}
                        disabled={!isBalanced || completeMutation.isPending}
                        className={cn(
                          'text-primary-foreground shadow-md',
                          isBalanced
                            ? 'bg-gradient-to-r from-success to-success/80 shadow-success/20'
                            : 'opacity-50 cursor-not-allowed bg-muted',
                        )}
                      >
                        <CheckCircle2 className="me-2 h-4 w-4" />
                        {completeMutation.isPending ? t('completing') : t('completeReconciliation')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Unreconciled transactions */}
              {selectedRec.status === 'DRAFT' &&
                (selectedRec.unreconciledTransactions?.length ?? 0) > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">
                        {t('unreconciledTransactions', { count: (selectedRec.unreconciledTransactions ?? []).length })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {(selectedRec.unreconciledTransactions ?? []).map((tx: BankTransaction) => (
                          <button
                            key={tx.id}
                            onClick={() => reconcileMutation.mutate({ txId: tx.id, reconcile: true })}
                            className="w-full flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-accent transition-colors text-sm text-start"
                            disabled={reconcileMutation.isPending}
                          >
                            <Square className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground text-xs w-24 flex-shrink-0">
                              {new Date(tx.date).toLocaleDateString()}
                            </span>
                            <span className="flex-1 truncate">{tx.description}</span>
                            <span className="text-xs text-muted-foreground">{tx.reference ?? ''}</span>
                            <span
                              className={cn(
                                'font-semibold',
                                tx.type === 'DEBIT' ? 'text-destructive-text' : 'text-success-text',
                              )}
                            >
                              {tx.type === 'DEBIT' ? '-' : '+'}{formatCurrency(tx.amount)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Reconciled transactions */}
              {(selectedRec.transactions?.length ?? 0) > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">
                      {t('reconciledTransactions', { count: (selectedRec.transactions ?? []).length })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {(selectedRec.transactions ?? []).map((tx: BankTransaction) => (
                        <div
                          key={tx.id}
                          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm"
                        >
                          {selectedRec.status === 'DRAFT' ? (
                            <button
                              onClick={() => reconcileMutation.mutate({ txId: tx.id, reconcile: false })}
                              disabled={reconcileMutation.isPending}
                              className="hover:text-muted-foreground transition-colors"
                            >
                              <CheckSquare className="h-4 w-4 text-success-text flex-shrink-0" />
                            </button>
                          ) : (
                            <CheckSquare className="h-4 w-4 text-success-text flex-shrink-0" />
                          )}
                          <span className="text-muted-foreground text-xs w-24 flex-shrink-0">
                            {new Date(tx.date).toLocaleDateString()}
                          </span>
                          <span className="flex-1 truncate text-muted-foreground">{tx.description}</span>
                          <span className="text-xs text-muted-foreground">{tx.reference ?? ''}</span>
                          <span
                            className={cn(
                              'font-semibold',
                              tx.type === 'DEBIT' ? 'text-destructive-text' : 'text-success-text',
                            )}
                          >
                            {tx.type === 'DEBIT' ? '-' : '+'}{formatCurrency(tx.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* New Reconciliation Dialog */}
      <Dialog
        visible={newRecDialogOpen}
        onHide={() => setNewRecDialogOpen(false)}
        header={t('startReconciliation')}
        style={{ width: '52rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setNewRecDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1 bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground shadow-md shadow-primary/20"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? t('creating') : t('startReconciliation')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t('bankAccount')} <span className="text-destructive-text">*</span>
            </Label>
            <Combobox
              options={bankAccounts.map((b: BankAccount) => ({ value: b.id, label: b.name }))}
              value={form.bankAccountId}
              onChange={(val) => setForm({ ...form, bankAccountId: val || '' })}
              placeholder={t('selectAccount')}
              emptyText={t('noBankAccountsYet')}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">
              {t('statementDate')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              type="date"
              value={form.statementDate}
              onChange={(e) => setForm({ ...form, statementDate: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">
                {t('openingBalance')} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={form.openingBalance}
                onChange={(e) => setForm({ ...form, openingBalance: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">
                {t('closingBalance')} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={form.closingBalance}
                onChange={(e) => setForm({ ...form, closingBalance: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Textarea
              className="min-h-[70px] resize-none"
              placeholder={t('notesPlaceholder')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
      </Dialog>
      <ConfirmDialog
        open={confirmComplete}
        onCancel={() => setConfirmComplete(false)}
        onConfirm={() => { completeMutation.mutate(); setConfirmComplete(false); }}
        title={t('completeReconciliation')}
        message={t('completeReconciliationMessage')}
        confirmLabel={t('complete')}
      />
    </div>
  );
}
